import { Dispatch, RefObject, SetStateAction } from "react";
import { Stack, useTheme } from "@mui/material";

import type { IngredientEcsBreakdown, ProductLivePreview } from "src/api/client-api/products/useProductLivePreview";
import { SectionCard, SectionHeader } from "src/components/EntityForm";
import type { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { SectionScoreBadge } from "./Layout/SectionScoreBadge";
import type { SectionErrors } from "./validation";
import { Step1Identity } from "./Steps/Step1Identity";
import { Step2AdditionalInformations } from "./Steps/Step2AdditionalInformations";
import { Step3Organization } from "./Steps/Step3Organization";
import { Step4Recipe } from "./Steps/Step4Recipe";
import { Step5Transformation } from "./Steps/Step5Transformation";
import { Step6Packaging } from "./Steps/Step6Packaging";
import { Step6Transport } from "./Steps/Step6Transport";
import { Step7Distribution } from "./Steps/Step7Distribution";
import { Step8Consumption } from "./Steps/Step8Consumption";
import type { TransportMaterializationPendingChange } from "./Steps/ProductTransportInlineSelector";
import type { WizardStepKey } from "./useWizardStepsV2";

const SECTIONS_WITH_SCORE: ReadonlySet<WizardStepKey> = new Set([
    "recipe",
    "transformation",
    "packaging",
    "transport",
    "distribution",
    "consumption",
]);

type Translate = (key: string, fallback: string) => string;

type ProductFormSectionsProps = {
    companyIds: { id: number; "@id": IRI; name: string };
    disabled: boolean;
    getDisplayedSectionScore: (key: WizardStepKey) => string | null;
    ingredientEcsBreakdown: Record<string, IngredientEcsBreakdown>;
    ingredientLineScores: Record<string, string | null>;
    onTransportMaterializationPendingChange: TransportMaterializationPendingChange;
    onPhotoPersistencePendingChange: (pending: boolean) => void;
    packagingLineScores: Record<string, string | null>;
    product: ProductFormType;
    refsByKey: Record<WizardStepKey, RefObject<HTMLDivElement | null>>;
    resolvedTransports: ProductLivePreview["resolvedTransports"] | undefined;
    scoreUnit: string;
    sectionErrors: SectionErrors;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    stepKeys: WizardStepKey[];
    t: Translate;
    transformationLineScores: Record<string, string | null>;
    transportLineScores: Record<string, string | null>;
};

export const ProductFormSections = (props: ProductFormSectionsProps) => {
    const {
        companyIds,
        disabled,
        getDisplayedSectionScore,
        ingredientEcsBreakdown,
        ingredientLineScores,
        onTransportMaterializationPendingChange,
        onPhotoPersistencePendingChange,
        packagingLineScores,
        product,
        refsByKey,
        resolvedTransports,
        scoreUnit,
        sectionErrors,
        setProduct,
        stepKeys,
        t,
        transformationLineScores,
        transportLineScores,
    } = props;
    const theme = useTheme();

    const renderSection = (key: WizardStepKey) => {
        switch (key) {
            case "identity":
                return (
                    <Step1Identity
                        product={product}
                        setProduct={setProduct}
                        disabled={disabled}
                        onPhotoPersistencePendingChange={onPhotoPersistencePendingChange}
                        questionnaireScore={getDisplayedSectionScore("identity")}
                        scoreUnit={scoreUnit}
                        errors={sectionErrors.identity ?? []}
                    />
                );
            case "additionalInformations":
                return <Step2AdditionalInformations product={product} setProduct={setProduct} />;
            case "organization":
                return <Step3Organization product={product} setProduct={setProduct} companyIds={companyIds} />;
            case "recipe":
                return (
                    <Step4Recipe
                        product={product}
                        setProduct={setProduct}
                        disabled={disabled}
                        errors={sectionErrors.recipe ?? []}
                        lineScores={ingredientLineScores}
                        transportLineScores={transportLineScores}
                        ingredientEcsBreakdown={ingredientEcsBreakdown}
                        resolvedTransports={resolvedTransports}
                        scoreUnit={scoreUnit}
                        onTransportMaterializationPendingChange={onTransportMaterializationPendingChange}
                    />
                );
            case "transformation":
                return (
                    <Step5Transformation
                        product={product}
                        setProduct={setProduct}
                        lineScores={transformationLineScores}
                        scoreUnit={scoreUnit}
                    />
                );
            case "packaging":
                return (
                    <Step6Packaging
                        product={product}
                        setProduct={setProduct}
                        errors={sectionErrors.packaging ?? []}
                        lineScores={packagingLineScores}
                        scoreUnit={scoreUnit}
                    />
                );
            case "transport":
                return (
                    <Step6Transport
                        product={product}
                        setProduct={setProduct}
                        disabled={disabled}
                        transportLineScores={transportLineScores}
                        resolvedTransports={resolvedTransports}
                        scoreUnit={scoreUnit}
                        onTransportMaterializationPendingChange={onTransportMaterializationPendingChange}
                    />
                );
            case "distribution":
                return <Step7Distribution product={product} setProduct={setProduct} />;
            case "consumption":
                return <Step8Consumption product={product} setProduct={setProduct} />;
        }
    };

    const sectionLabels: Record<WizardStepKey, { title: string; subtitle?: string }> = {
        identity: {
            title: t("product.v2.informations.identity", "Identity"),
            subtitle: t("product.v2.identity.section_subtitle", "Information needed to identify your product"),
        },
        additionalInformations: {
            title: t("product.v2.additional_informations.title", "Additional informations"),
        },
        organization: {
            title: t("product.v2.organization.section_title", "Organization"),
            subtitle: t(
                "entity.organization.product.section_subtitle",
                "Define the product's positioning within your organization and the entities that will be able to contribute to it.",
            ),
        },
        recipe: {
            title: t("product.recipe.title", "Recipe"),
            subtitle: t(
                "product.v2.recipe.section_subtitle",
                "Add the ingredients and their weight before and after processing.",
            ),
        },
        transformation: {
            title: t("product.v2.transformation.placeholder.title", "Processing"),
            subtitle: t(
                "product.v2.transformation.section_subtitle",
                "Main processing method applied to your product.",
            ),
        },
        packaging: {
            title: t("sidebar.packagings.title", "Packaging"),
            subtitle: t(
                "product.v2.packaging.quantity_section_subtitle",
                "Add the different packaging of your product and their quantity.",
            ),
        },
        transport: {
            title: t("transport", "Transport"),
        },
        distribution: {
            title: t("product.distribution.title", "Distribution"),
            subtitle: t("product.v2.distribution.section_subtitle", "Select how your product is distributed."),
        },
        consumption: {
            title: t("product.consumption.title", "Consumption"),
            subtitle: t(
                "product.v2.consumption.preparation_techniques_subtitle",
                "Select the different preparation techniques for your product.",
            ),
        },
    };

    return (
        <Stack
            component="fieldset"
            disabled={disabled}
            aria-busy={disabled || undefined}
            sx={{
                border: 0,
                gap: theme.spacing(2),
                margin: 0,
                minWidth: 0,
                padding: 0,
                width: "100%",
            }}
        >
            {stepKeys.map((key, index) => {
                const hasScore = SECTIONS_WITH_SCORE.has(key);
                const displayedScore = hasScore ? getDisplayedSectionScore(key) : null;

                return (
                    <SectionCard
                        key={key}
                        ref={refsByKey[key]}
                        data-section-key={key}
                        sx={sectionErrors[key] ? { borderColor: theme.palette.error.main } : undefined}
                    >
                        <SectionHeader
                            progress={{ current: index + 1, total: stepKeys.length }}
                            title={sectionLabels[key].title}
                            subtitle={sectionLabels[key].subtitle}
                            rightSlotAlignment={displayedScore === null ? "start" : "center"}
                            rightSlot={
                                hasScore ? <SectionScoreBadge value={displayedScore} unit={scoreUnit} /> : undefined
                            }
                        />
                        {renderSection(key)}
                    </SectionCard>
                );
            })}
        </Stack>
    );
};
